import axios from 'axios';
import { setUser, setMaintenanceBypassToken } from '../reducers/User';
import { isEqual, isEmpty } from 'lodash';
import { dispatch, getState } from '../config/store';
import moment from 'moment';
import config from 'config/config';
import { setUserPlans } from 'reducers/AccessController';
import { USER_PLANS } from 'config/appConfig';
import { setHomePageRegion } from 'reducers/HomePage';
import { homePageWidgetRegions, typesSelection, searchIndexs } from 'config/filterTypes';
import { setWatchlistType } from 'reducers/Watchlist';
import { setSearchIndex } from 'reducers/Topic';
import { getWatchlistSettings } from 'components/watchlist/WatchlistHelpers';
import { getItemFromLocalStorage, removeLocalStorage } from 'utils/helpers';
import { setScreenerRegion, setSelectedClass } from 'reducers/Screener';
import { assetClasses } from 'config/watchlistFileTyes';

let companyOptionsRequestToken = null;

export const signOutUser = async path => {
  try {
    await axios.delete('/api/users/sign_out');
  } catch (error) {
    console.log(error);
  } finally {
    removeLocalStorage();
    window.location.href = path;
  }
};

export const checkIsSiteUnderMaintenanc = async () => {
  try {
    const byPassKey = new URLSearchParams(window.location.search).get(config.maintenanceByPassKey);
    if (byPassKey) {
      dispatch(setMaintenanceBypassToken(byPassKey));
    }
    const res = await axios.get('/api/backend_status');
    return res.data?.data?.maintenance_status ?? null;
  } catch (error) {
    console.log('error', error);
  }
};

export const getUserByToken = async () => {
  try {
    const response = await axios.get('/api/users/get_user');
    if (response?.data?.error === false && response?.data?.data) {
      const isNoPlan = extractUserRoutesByPlan(response.data.plan);
      if (isNoPlan) return null;

      dispatch(setUser(response.data.data));
      return true;
    }
  } catch (e) {
    console.log('error', e);
  }
};

export const setReacntIdAndDocumentType = company => {
  const last10kDate = moment(company?.last10k);
  const last10qDate = moment(company?.last10q);
  if (isEmpty(company) || !last10kDate.isValid() || !last10qDate.isValid()) return;

  const { recentId10k, recentId10q } = company;

  if (last10kDate.isAfter(last10qDate)) {
    company.recentId = recentId10k || recentId10q; // recentId required in sentiment
    company.documentType = recentId10k ? '10-K' : '10-Q'; // documentType required in comparison
  } else {
    company.recentId = recentId10q || recentId10k;
    company.documentType = recentId10q ? '10-Q' : '10-K';
  }
};

export const getGlobalSearchOptions = async (searchText, mustTickerAndCompId = false, index = undefined) => {
  try {
    if (companyOptionsRequestToken) {
      companyOptionsRequestToken.cancel();
    }
    if (!searchText) return;
    const cancelToken = axios.CancelToken.source();
    companyOptionsRequestToken = cancelToken;
    const response = await axios.get('/api/get_companies_list', {
      params: {
        search: searchText,
        mustTickerAndCompId,
        selected_region: config.domainName === 'TMX' ? 15 : 2,
        index,
        size: 500,
      },
      cancelToken: cancelToken.token,
    });
    companyOptionsRequestToken = null;
    if (response?.data?.data) {
      return response.data.data.map(option => ({
        companyId: option.company_id,
        ticker: option.ticker ?? null,
        companyName: option.company_name ?? '',
        sector: option.sector ?? '',
        industry: option.industry ?? '',
        sourceName: option.source_name,
        flag: option.source_social_id,
        globalDropdownLabel: `${option.ticker ?? ''} - ${option.company_name ?? ''} - ${option.country_code ?? ''}`,
      }));
    }
  } catch (e) {
    console.log('error', e);
  }
};

export const updateUser = async (payload, updateState = true) => {
  try {
    const response = await axios.post('/api/users/update_user', payload);
    if (response?.data?.data && response?.data?.error === false) {
      if (updateState) {
        dispatch(setUser(response.data.data));
      }
    }
    return response;
  } catch (e) {
    console.log('error', e);
  }
};

/*
    Below function helps to show tooltio in 'Highcharts' after 'delay' time.
    'delay' time is defined in the 'tooltip' object inside 'options' that are 
    passed to 'Highcharts'. This also hide tooltip when pointer leaves the graph.
*/
export const addDelayInTooltip = Highcharts => {
  (H => {
    if (H && H.Tooltip?.prototype) {
      H.wrap(H.Tooltip.prototype, 'refresh', function(proceed, point, mouseEvent) {
        const tooltip = this;
        if (
          mouseEvent &&
          !tooltip.chart.isInsidePlot(
            mouseEvent.chartX - tooltip.chart.plotLeft,
            mouseEvent.chartY - tooltip.chart.plotTop
          )
        ) {
          tooltip.hide();
          clearTimeout(tooltip.hideTimer);
          tooltip.hideTimer = null;
          clearTimeout(tooltip.showTimer);
          tooltip.showTimer = null;
          return;
        }
        if (this.options.delay) {
          !isEqual(point, this.queuePoint) && clearTimeout(this.showTimer);
          tooltip.hide();
          this.queuePoint = point;
          this.showTimer && clearTimeout(this.showTimer);
          this.hideTimer && clearTimeout(this.hideTimer);
          if (tooltip && point && mouseEvent) {
            tooltip.hide();
            this.showTimer = setTimeout(() => proceed.call(tooltip, point, mouseEvent), this.options.delay);
          }
        } else {
          proceed.call(this, point, mouseEvent);
        }
      });

      H.wrap(H.Tooltip.prototype, 'hide', function(proceed) {
        clearTimeout(this.showTimer);
        this.showTimer = null;
        clearTimeout(this.hideTimer);
        this.hideTimer = null;
        proceed.call(this);
      });
    }
  })(Highcharts);
};

export const extractUserRoutesByPlan = userPlans => {
  const userAllowedPlans = {};

  if (userPlans?.length) {
    userPlans.forEach(plan => {
      userAllowedPlans[plan.product_name] = plan.product_value;
    });
  }

  const hasUDTPlan = userAllowedPlans[USER_PLANS.udt] === 'true';

  if (hasUDTPlan) {
    dispatch(setUserPlans(userAllowedPlans));
    setDefaultRegionStates(userAllowedPlans);
  } else {
    signOutUser('/accessDenied');
    return true;
  }
};

export const extractItemsByUserPlan = (userPlans, allItems, defaultValue = []) => {
  if (!userPlans) {
    userPlans = getState().AccessController.userPlans;
  }

  if (isEmpty(userPlans) || isEmpty(allItems)) return defaultValue;

  return allItems.filter(({ license }) => {
    return license === USER_PLANS.noLicenseNeeded || userPlans[license] === 'true';
  });
};

export const doesUserHaveSpecifiedPlan = (userPlans, plan) => {
  if (!userPlans) {
    userPlans = getState().AccessController.userPlans;
  }

  return userPlans[plan] === 'true';
};

const setDefaultRegionStates = userPlans => {
  const allowedHomepageRegions = extractItemsByUserPlan(userPlans, homePageWidgetRegions);
  if (allowedHomepageRegions.length) {
    dispatch(setHomePageRegion(allowedHomepageRegions[0]));
  }

  const allowedWatchlistRegions = extractItemsByUserPlan(userPlans, typesSelection);
  if (allowedWatchlistRegions.length) {
    const allowedRegionKeys = allowedWatchlistRegions.map(region => region.key);
    const selectedType = getWatchlistSettings()['selectedType'];
    dispatch(setWatchlistType(allowedRegionKeys.includes(selectedType) ? selectedType : allowedRegionKeys[0]));

    const screenerRegion = getItemFromLocalStorage('screenerSetting', {})['screenerRegion'];
    const sclectedClass = getItemFromLocalStorage('screenerSetting', {})['sclectedClass'];
    const isValidScreenerRegion = allowedRegionKeys.includes(screenerRegion);
    dispatch(setScreenerRegion(isValidScreenerRegion ? screenerRegion : allowedRegionKeys[0]));
    dispatch(setSelectedClass(isValidScreenerRegion ? sclectedClass : assetClasses[allowedRegionKeys[0]][0].value));
  }

  const allowedSearchIndexes = extractItemsByUserPlan(userPlans, searchIndexs);
  if (allowedSearchIndexes.length) {
    dispatch(setSearchIndex(allowedSearchIndexes[0]));
  }
};
